import {
    formatAddress,
    formatDate,
    formatGender,
    formatNameLastFirst,
    openInNewTab,
} from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndRowContent } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import { ClinicalInformation } from 'components/referralHistory/ClinicalInformation';
import { ClinicalPolicyDialog } from 'components/referralHistory/dialogs/ClinicalPolicyDialog';
import { ContactInfoDialog } from 'components/referralHistory/dialogs/ContactInfoDialog';
import { PrescribingInfo } from 'components/referralHistory/PrescribingInfo';
import { ReferralDetails } from 'components/referralHistory/ReferralDetails';
import { ServicingSupplierDetails } from 'components/referralHistory/ServicingSupplierDetails';
import { PrintHeader } from 'components/shared/PrintHeader';
import { useEligibleSuppliers, useSupplierDetails } from 'services/APIHooks';
import { AppRoutes } from 'services/routes';
import { reauthReferral } from 'store/referralForm/referralFormSlice';
import {
    fetchReferralDetails,
    fetchReferralHistory,
} from 'store/referralHistory/referralHistorySlice';

export const ReferralDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [showContactInfoDialog, setShowContactInfoDialog] = useState(false);
    const [showClinicalPolicyDialog, setShowClinicalPolicyDialog] = useState(false);

    // get selected referral from referral list
    const referralsDataLoaded = useSelector((state) => state.referralHistory.referralsDataLoaded);
    const selectedReferral = useSelector((state) =>
        state.referralHistory.referralsData.find((referral) => referral.referralId === id),
    );

    // fetch referral list if navigating directly to page since it will not be loaded yet
    useEffect(() => {
        if (!referralsDataLoaded) {
            dispatch(fetchReferralHistory());
        }
    }, [referralsDataLoaded, dispatch]);

    // navigate back to referral history list if id is invalid
    useEffect(() => {
        if (!selectedReferral && referralsDataLoaded) {
            navigate(AppRoutes.referralHistory, { replace: true });
        }
    }, [selectedReferral, referralsDataLoaded, navigate]);

    // load referral details
    const referralDetails = useSelector((state) => state.referralHistory.referralDetails[id]);
    useEffect(() => {
        if (selectedReferral) {
            dispatch(
                fetchReferralDetails(id, selectedReferral.payerId, selectedReferral.prescriberId),
            );
        }
    }, [id, selectedReferral, dispatch]);

    // load supplier details
    const referralAtFMH = selectedReferral?.referralStatus === ReferralStatusIndicator.Statuses.FMH;
    const referralPending =
        selectedReferral?.referralStatus === ReferralStatusIndicator.Statuses.PENDINTAKE;
    const supplierDetails = useSupplierDetails(selectedReferral?.supplierNpi);
    const eligibleSuppliers = useEligibleSuppliers({
        payerId: selectedReferral?.payerId,
        segmentId: selectedReferral?.segmentId,
        memberState: selectedReferral?.memberState,
        productServiceOid: selectedReferral?.productServiceOid,
        benefit: referralDetails?.benefit,
        placeOfServce: selectedReferral?.placeOfServce,
        fetchValues: referralAtFMH,
    });

    // Actions button menu items
    const menuItems = [
        {
            text: 'View Patient Contact Information',
            onItemClick: () => setShowContactInfoDialog(true),
        },
        ...(referralDetails?.clinicalInfo?.clinicalPolicyAnswers?.length > 0
            ? [
                  {
                      text: 'View Clinical Policy',
                      onItemClick: () => setShowClinicalPolicyDialog(true),
                  },
              ]
            : []),
        {
            text: 'Print Referral',
            onItemClick: () => window.print(),
        },
        {
            text: 'See Terms & Conditions',
            onItemClick: () => openInNewTab(AppRoutes.termsAndConditions),
        },
    ];

    const additionalMenuItems =
        selectedReferral?.referralStatus === ReferralStatusIndicator.Statuses.PENDREAUTH
            ? [
                  {
                      text: 'Reauth Referral',
                      onItemClick: () => {
                          dispatch(reauthReferral(referralDetails));
                          navigate(AppRoutes.referralForm);
                      },
                  },
              ]
            : undefined;

    return (
        <>
            <FullscreenLoading
                isShowing={!selectedReferral}
                loadingText="Loading referral information..."
            />
            <ContactInfoDialog
                referralData={referralDetails}
                isOpen={showContactInfoDialog}
                onClose={() => setShowContactInfoDialog(false)}
            />
            <ClinicalPolicyDialog
                referralData={referralDetails}
                isOpen={showClinicalPolicyDialog}
                onClose={() => setShowClinicalPolicyDialog(false)}
            />
            <FormHeaderAndRowContent
                rowOneContentHeader={
                    <>
                        <ActionButtonText
                            label="Back To Referrals"
                            isBackNavigationButton
                            onClick={() => navigate(AppRoutes.referralHistory)}
                        />
                        <div style={{ marginLeft: 'auto' }}>
                            <ButtonWithDropdownMenu
                                menuItems={menuItems}
                                additionalMenuItems={additionalMenuItems}
                                intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                                label="Actions"
                            />
                        </div>
                    </>
                }
                rowTwoContentHeader={
                    selectedReferral?.referralStatus ===
                        ReferralStatusIndicator.Statuses.PENDREAUTH && (
                        <InlineMessage
                            intent={InlineMessage.Intent.WARNING}
                            label="This referral is pending reauth. Reauth this referral from the actions menu."
                        />
                    )
                }
                rowThreeContentHeader={
                    referralDetails && (
                        <>
                            <div className="print__header" style={{ display: 'none' }}>
                                <PrintHeader pageTitle="Authorized Referral" />
                            </div>
                            <div
                                className="print__patient-details sds_form-section__title"
                                style={{ display: 'none' }}
                            >
                                PATIENT DETAILS
                            </div>
                            <FormTitleLabelValuePair
                                label={formatNameLastFirst(
                                    referralDetails.memberLastName,
                                    referralDetails.memberFirstName,
                                )}
                                value={formatAddress(
                                    referralDetails.memberCity,
                                    referralDetails.memberState,
                                    referralDetails.memberZip,
                                )}
                            />
                            <HeaderLabelValuePair
                                label="DOB"
                                value={formatDate(referralDetails.memberDOB)}
                            />
                            <HeaderLabelValuePair
                                label="Gender"
                                value={formatGender(referralDetails.memberGender)}
                            />
                            <HeaderLabelValuePair
                                label="Health Plan"
                                value={referralDetails.healthPlan}
                            />
                            <HeaderLabelValuePair
                                label="Cardholder ID"
                                value={referralDetails.memberId}
                            />
                        </>
                    )
                }
                rowOneContent={
                    <ServicingSupplierDetails
                        supplierDetails={supplierDetails}
                        referralAtFMH={referralAtFMH}
                        referralPending={referralPending}
                        eligibleSuppliers={eligibleSuppliers}
                    />
                }
                rowTwoContent={<PrescribingInfo referralDetails={referralDetails} />}
                rowThreeContent={
                    <>
                        <ReferralDetails
                            selectedReferral={selectedReferral}
                            referralDetails={referralDetails}
                        />
                        <ClinicalInformation referralDetails={referralDetails} />
                    </>
                }
            />
        </>
    );
};
